import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateProcess } from '../api/processes.js';
import {
    PROCESS_TYPES,
    buildProcessConfigMetadata,
    defaultFieldValues,
    monthLabel,
    processTypeFields,
    processTypeLabel,
} from '../utils/constants.js';
import Modal from './Modal.jsx';
import ErrorBanner from './ErrorBanner.jsx';

export default function CreateProcessModal({ companyId, onClose, onCreated }) {
    const { t } = useTranslation();
    const creatableTypes = PROCESS_TYPES.filter((pt) => pt.creatable !== false);
    const [type, setType] = useState(creatableTypes[0].code);
    const [fieldValues, setFieldValues] = useState(() => defaultFieldValues(creatableTypes[0].code));

    const { mutate, isPending, error } = useCreateProcess(companyId, (data) => {
        onCreated?.(data);
        onClose();
    });

    const fields = processTypeFields(type);

    function handleTypeChange(newType) {
        setType(newType);
        setFieldValues(defaultFieldValues(newType));
    }

    function updateField(key) {
        return (e) => setFieldValues((v) => ({ ...v, [key]: e.target.value }));
    }

    const missingRequired = fields.some((f) => f.required && !fieldValues[f.key]?.trim());

    function handleSubmit(e) {
        e.preventDefault();
        if (missingRequired) return;
        mutate({ type, configMetadata: buildProcessConfigMetadata(type, fieldValues), enabled: true });
    }

    const realTypes = creatableTypes.filter((pt) => pt.group === 'Processes');
    const testTypes = creatableTypes.filter((pt) => pt.group === 'Test');

    return (
        <Modal title={t('processes.modalTitle')} onClose={onClose}>
            <form onSubmit={handleSubmit}>
                <ErrorBanner error={error} fallback={t('processes.createError')} />

                <div className="form-row">
                    <label htmlFor="process-type">{t('processes.typeLabel')}</label>
                    <select id="process-type" value={type} onChange={(e) => handleTypeChange(e.target.value)}>
                        <optgroup label={t('processes.groupProcesses')}>
                            {realTypes.map((pt) => (
                                <option key={pt.code} value={pt.code}>
                                    {processTypeLabel(pt.code, t)}
                                </option>
                            ))}
                        </optgroup>
                        <optgroup label={t('processes.groupTest')}>
                            {testTypes.map((pt) => (
                                <option key={pt.code} value={pt.code}>
                                    {processTypeLabel(pt.code, t)}
                                </option>
                            ))}
                        </optgroup>
                    </select>
                </div>

                {fields.map((field) => (
                    <div className="form-row" key={field.key}>
                        <label htmlFor={`process-field-${field.key}`}>
                            {t(field.labelKey)}
                            {!field.required && <span className="hint"> {t('common.optional')}</span>}
                        </label>
                        {field.type === 'select' ? (
                            <select
                                id={`process-field-${field.key}`}
                                value={fieldValues[field.key] ?? ''}
                                onChange={updateField(field.key)}
                                required={field.required}
                            >
                                <option value="" disabled>
                                    {t('common.select')}
                                </option>
                                {field.options.map((optionValue) => (
                                    <option key={optionValue} value={optionValue}>
                                        {field.optionsNamespace === 'months' ? monthLabel(optionValue, t) : optionValue}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <input
                                id={`process-field-${field.key}`}
                                type={field.type === 'number' ? 'number' : 'text'}
                                value={fieldValues[field.key] ?? ''}
                                onChange={updateField(field.key)}
                                required={field.required}
                            />
                        )}
                        {field.hintKey && <span className="hint">{t(field.hintKey)}</span>}
                    </div>
                ))}

                <div className="modal-actions">
                    <button type="button" className="btn" onClick={onClose}>
                        {t('common.cancel')}
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isPending || missingRequired}>
                        {isPending ? t('processes.creating') : t('processes.create')}
                    </button>
                </div>
            </form>
        </Modal>
    );
}
